'use client';

import { type FC, useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRuntime } from '@/runtime/RuntimeContext';
import { withLangPrefix } from '@/i18n/locale';
import { languageSwitchTarget, useLanguageAlternatesStore } from '@/i18n/languageAlternates';
import { isWikiPath } from '@/lib/wikiPath';
import { FaSignOutAlt, FaPlus, FaCoins, FaChevronDown, RiBookOpenLine } from '../icons';
import { Credits } from '../Credits';
import { useProfile, useBalance } from '../../queries/useProfileQueries';
import { useActiveBuff, useGlobalBuff } from '../../queries/useHomeQueries';
import { useOnlineCount } from '../../queries/useOnlineCount';
import { useOnlineCountSubscription } from '../../hooks/useOnlineCountSubscription';
import { useLanguage } from '../../hooks/useLanguage';
import { useLocalizedHref } from '../../hooks/useLocalizedHref';
import { useIntentPrefetch } from '../../hooks/useIntentPrefetch';
import { languageCodes, languageFlags, languages } from '../../stores/languageStore';
import { useCapability } from '@/runtime/useCapability';
import { useLogout } from '../../hooks/useLogout';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { useNavigation } from '../../contexts/NavigationContext';
import { useServerHasAuth } from '../../contexts/ServerAuthContext';
import { useAuthStore } from '../../stores/authStore';
import { useRawInitDataReady } from '../../hooks/useRawInitDataWithDev';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCurrentTime } from '../../hooks/useCurrentTime';
import { formatTimeRemaining } from '../../utils/formatters';
import { cn } from '../../utils/cn';
import translations from './DesktopHeader.translations';

const LANG_MENU_CLASS =
  'absolute top-full end-0 mt-1 bg-[#0c0f16] border border-white/[0.08] rounded-xl overflow-hidden z-50 min-w-[140px] shadow-[0_12px_40px_rgba(0,0,0,0.7)]';

const langItemClass = (active: boolean) =>
  cn(
    'flex items-center gap-2 w-full px-3 py-2 text-xs border-none cursor-pointer transition-colors text-start',
    active ? 'bg-white/[0.06] text-white' : 'bg-transparent text-white/50 hover:bg-white/[0.03] hover:text-white/80',
  );

export const DesktopHeader: FC = () => {
  const { data: profile, isError: profileFailed } = useProfile();
  const balance = useBalance();
  const { data: activeBuff } = useActiveBuff();
  const { data: globalBuff } = useGlobalBuff();
  const { data: onlineCountApi } = useOnlineCount();
  const onlineCountWs = useOnlineCountSubscription();
  const onlineCount = onlineCountWs ?? onlineCountApi;
  const { language, changeLanguage } = useLanguage();
  const canLogout = useCapability('logoutAction');
  const logout = useLogout();
  const navigation = useNavigation();
  const { t } = useLocalTranslation(translations);
  const localize = useLocalizedHref();
  const isWeb = useRuntime() === 'web';
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const languageAlternates = useLanguageAlternatesStore((state) => state.alternates);
  const onWiki = isWikiPath(pathname);
  const intentPrefetch = useIntentPrefetch();
  const linkPrefetch = (href: string) => (onWiki ? intentPrefetch(href) : {});
  const wikiHref = localize('/wiki');
  const loginHref = localize('/login');

  const serverHasAuth = useServerHasAuth();
  const hydrated = useAuthStore((s) => s.hydrated);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const initDataReady = useRawInitDataReady();
  const authResolved = hydrated && initDataReady;
  const isAuthed = authResolved ? !!profile : serverHasAuth === true;
  // WHY: без ответа сервера о входе (вики) до /auth/me и профиля не рисуем ни «Войти», ни баланс — вошедший увидел бы
  // мигание кнопки входа
  const authPending = serverHasAuth === null && (!authResolved || (isAuthenticated && !profile && !profileFailed));

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const now = useCurrentTime();

  const showPromocodeBuff = (activeBuff && activeBuff.isValid) || (globalBuff && globalBuff.isActive);
  const buffExpiresAt = activeBuff?.expiresAt || globalBuff?.endDate;
  const buffPercentage = activeBuff?.bonusPercentage || globalBuff?.bonusPercentage || 0;
  const buffRemaining = buffExpiresAt ? new Date(buffExpiresAt).getTime() - now : 0;
  const buffTimerStr = showPromocodeBuff && buffRemaining > 0 ? formatTimeRemaining(buffRemaining) : null;

  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (e.target instanceof Node && langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  return (
    <header className="border-b border-white/[0.06] h-[72px] flex items-center justify-between shrink-0 ps-[calc(1.5rem+var(--tg-viewport-safe-area-inset-left,0px))] pe-[calc(1.5rem+var(--tg-viewport-safe-area-inset-right,0px))]">

      <div className="flex items-center gap-4">
        <Link
          href={localize('/')}
          data-site-home-link
          aria-label="SHOT"
          className="font-sans text-[27px] tracking-widest uppercase select-none no-underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-purple"
          style={{
            fontWeight: 800,
            letterSpacing: '0.18em',
            background: 'linear-gradient(to top, #7C3AED 0%, #A78BFA 50%, #00C8FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.5)) drop-shadow(0 0 10px rgba(0,200,255,0.3))',
          }}
        >
          SHOT
        </Link>
        {onlineCount != null && onlineCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
            <span className="text-sm text-accent-green/80 font-medium tracking-wide">online</span>
            <span className="text-sm font-bold text-white/50 tabular-nums">{onlineCount}</span>
          </div>
        )}
      </div>


      <div className="flex items-center gap-3">

        {isWeb && (
          <Link
            href={wikiHref}
            {...linkPrefetch(wikiHref)}
            aria-current={onWiki ? 'page' : undefined}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium hover:text-white hover:bg-white/[0.05] transition-colors',
              onWiki ? 'text-white bg-white/[0.06]' : 'text-white/60',
            )}
          >
            <RiBookOpenLine aria-hidden className="text-base" />
            {t('wiki')}
          </Link>
        )}

        <div ref={langRef} className="relative">
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-transparent hover:bg-white/[0.05] border-none cursor-pointer transition-colors"
            onClick={() => setLangOpen(!langOpen)}
          >
            <span className="text-lg">{languageFlags[language]}</span>
            <FaChevronDown className="text-[9px] text-white/30" />
          </button>
          {(isWeb || langOpen) && (
            <div className={cn(LANG_MENU_CLASS, !langOpen && 'hidden')}>
              {languageCodes.map((code) =>
                isWeb ? (
                  <a
                    key={code}
                    href={languageSwitchTarget(code, languageAlternates, withLangPrefix(code, pathname) + (search ? `?${search}` : ''))}
                    hrefLang={code}
                    className={langItemClass(code === language)}
                    onClick={(e) => { e.preventDefault(); changeLanguage(code); setLangOpen(false); }}
                  >
                    <span>{languageFlags[code]}</span>
                    <span>{languages[code]}</span>
                  </a>
                ) : (
                  <button
                    key={code}
                    className={langItemClass(code === language)}
                    onClick={() => { changeLanguage(code); setLangOpen(false); }}
                  >
                    <span>{languageFlags[code]}</span>
                    <span>{languages[code]}</span>
                  </button>
                ),
              )}
            </div>
          )}
        </div>


        <div className="w-px h-7 bg-white/[0.06]" />

        {authPending ? (
          <div aria-hidden className="h-9 w-20" />
        ) : isAuthed ? (
          <>

            <button
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] cursor-pointer transition-colors"
              onClick={() => navigation.navigate('payment')}
            >
              <FaCoins className="text-price text-sm" />
              <Credits
                amount={balance}
                animateChange
                showIcon={false}
                className="text-white font-bold text-base tabular-nums tracking-tight"
              />
              <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-accent-green ms-1">
                <FaPlus className="text-white text-[10px]" />
              </div>
            </button>


            {showPromocodeBuff && (
              <button
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-accent-green/10 border border-accent-green/20 cursor-pointer transition-colors hover:bg-accent-green/20"
                onClick={() => navigation.navigate('payment')}
              >
                <span className="text-sm font-bold text-accent-green">+{buffPercentage}%</span>
                {buffTimerStr && <span className="text-xs text-accent-green/60 tabular-nums">{buffTimerStr}</span>}
              </button>
            )}


            <div className="w-px h-7 bg-white/[0.06]" />


            <button
              className="flex items-center justify-center p-1 rounded-full bg-transparent hover:bg-white/[0.04] border-none cursor-pointer transition-all hover:scale-105"
              onClick={() => { navigation.changeTab('profile'); navigation.resetStack('profile', 'profile'); }}
            >
              <img
                src={profile?.id ? `${getApiBaseUrl()}/api/users/avatar/${profile.id}` : undefined}
                alt=""
                className="w-9 h-9 rounded-full object-cover bg-white/10"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </button>


            {canLogout && (
              <button
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent hover:bg-white/[0.05] border-none cursor-pointer transition-colors text-white/25 hover:text-accent-red"
                onClick={logout}
              >
                <FaSignOutAlt size="14" />
              </button>
            )}
          </>
        ) : (
          <Link
            href={loginHref}
            {...linkPrefetch(loginHref)}
            className="px-4 py-2 rounded-2xl bg-accent-green text-white font-semibold text-sm hover:opacity-90 transition-opacity"
          >
            {t('login')}
          </Link>
        )}
      </div>
    </header>
  );
};
